import { useForm } from '@inertiajs/react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastFlash } from '@/lib/flash-toast';
import type { JudgeAdmin, JudgingStageOption } from '@/types';

interface JudgeFormDialogProps {
  eventSlug: string;
  // Absent when creating a judge.
  judge?: JudgeAdmin;
  stages: JudgingStageOption[];
  open: boolean;
  onClose: () => void;
}

const FIELDS = [
  {
    key: 'fullName',
    label: 'Full Name',
    placeholder: 'Jane Doe',
  },
  {
    key: 'github',
    label: 'GitHub URL',
    placeholder: 'https://github.com/username',
  },
  {
    key: 'linkedin',
    label: 'LinkedIn URL (optional)',
    placeholder: 'https://linkedin.com/in/username',
  },
  {
    key: 'companyTitle',
    label: 'Company / Title (optional)',
    placeholder: 'CTO at Acme Inc',
  },
] as const;

// The create and edit dialogs are the same four fields; mount it keyed on the judge so
// the form starts from that row.
export function JudgeFormDialog({
  eventSlug,
  judge,
  stages,
  open,
  onClose,
}: JudgeFormDialogProps) {
  const form = useForm({
    fullName: judge?.fullName ?? '',
    github: judge?.github ?? '',
    linkedin: judge?.linkedin ?? '',
    companyTitle: judge?.companyTitle ?? '',
    stageIds: judge?.stageIds ?? [],
  });
  const { data, setData, processing } = form;
  const errors = form.errors as unknown as Record<string, string | undefined>;
  const verb = judge ? 'update' : 'create';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const options = {
      preserveScroll: true,
      onSuccess: (page: Parameters<typeof toastFlash>[0]) => {
        toastFlash(page, judge ? 'Judge updated' : 'Judge created');
        form.reset();
        onClose();
      },
      onError: () =>
        toast({ type: 'error', description: `Failed to ${verb} judge` }),
    };

    if (judge) {
      form.patch(`/admin/${eventSlug}/judging/judges/${judge.id}`, options);
    } else {
      form.post(`/admin/${eventSlug}/judging/judges`, options);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{judge ? 'Edit Judge' : 'Add New Judge'}</DialogTitle>
          <DialogDescription>
            The judge signs in at /judges with this GitHub account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {FIELDS.map((field) => (
            <div key={field.key} className="grid gap-2">
              <Label htmlFor={`judge-${field.key}`}>{field.label}</Label>
              <Input
                id={`judge-${field.key}`}
                placeholder={field.placeholder}
                value={data[field.key]}
                onChange={(e) => setData(field.key, e.target.value)}
              />
              {errors[field.key] && (
                <p className="text-destructive text-sm">{errors[field.key]}</p>
              )}
            </div>
          ))}
          <div className="grid gap-2">
            <Label>Stages they grade</Label>
            {stages.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                This edition has no stages yet. Create them in Stages.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {stages.map((stage) => (
                  <label
                    key={stage.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <Checkbox
                      checked={data.stageIds.includes(stage.id)}
                      onCheckedChange={(checked) =>
                        setData(
                          'stageIds',
                          checked
                            ? [...data.stageIds, stage.id]
                            : data.stageIds.filter((id) => id !== stage.id),
                        )
                      }
                    />
                    {stage.name}
                  </label>
                ))}
              </div>
            )}
            {errors.stageIds && (
              <p className="text-destructive text-sm">{errors.stageIds}</p>
            )}
          </div>
          {errors.base && !FIELDS.some((field) => errors[field.key]) && (
            <p className="text-destructive text-sm">{errors.base}</p>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing
                ? judge
                  ? 'Saving...'
                  : 'Creating...'
                : judge
                  ? 'Save Changes'
                  : 'Create Judge'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
